/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { FC } from 'react';
import {
  Box,
  Divider,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { TEMPLATES_EDITOR_REGARCY } from 'src/constants/templates';
import { TEMPLATES_EDITOR_GUIDE, GuideSpec } from 'src/constants/templatesGuide';

import EditMulti from 'src/components/editor/EditMultiRegurcy';
import EditButtonType from 'src/components/editor/EditButtonType';
import EditBgDimmed from 'src/components/editor/EditBgImageBlur/EditBgImageBlur';
import SwitchAutoRolling from 'src/components/editor/SwitchAutoRolling';
import SwitchDarkMode from 'src/components/editor/SwitchDarkMode';
import SwitchSlidePosition from 'src/components/editor/SwitchSlidePosition';
import SwitchImageType from 'src/components/editor/SwitchImageType';
import SwitchMediaType from 'src/components/editor/SwitchMediaType';
import Button from './Button';
import Text from './Text';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    minWidth: '100%',
    height: '100%',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    overflow: 'hidden',
    border: '1px solid #E5E8EB'
  },
  tabScroll: {
    width: '100%',
    minWidth: '100%',
    height: 'calc(100% - 56px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    backgroundColor: '#FFFFFF',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  tabsWrapper: {
    width: '100%',
    minHeight: '56px',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E5E8EB'
  },
  tabs: {
    minHeight: '56px',
    padding: '0 20px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    color: '#8B95A1',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '13px',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },

  actTab: {
    cursor: 'pointer',
    display: 'flex',
    margin: 'auto 0',
    padding: '8px 12px',
    flexDirection: 'column',
    color: '#FFFFFF',
    fontWeight: 600,
    letterSpacing: '0.02em',
    backgroundColor: '#191F28',
    borderRadius: '8px',
    transition: 'all 0.2s ease'
  },
  tab: {
    cursor: 'pointer',
    display: 'flex',
    margin: 'auto 0',
    padding: '8px 12px',
    flexDirection: 'column',
    color: '#4E5968',
    fontWeight: 500,
    letterSpacing: '0.02em',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#191F28',
      backgroundColor: '#F2F3F5'
    }
  },
  guideTabWrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  actGuideTab: {
    width: '100%',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    color: '#191F28',
    borderBottom: '2px solid #191F28',
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: '8px',
    transition: 'all 0.2s ease'
  },
  guideTab: {
    width: '100%',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    color: '#8B95A1',
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#4E5968'
    }
  },
  guideContainer: {
    padding: '20px 16px'
  },
  guideSection: {
    marginBottom: '24px'
  },
  guideSectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#191F28',
    marginBottom: '8px',
    fontFamily: "'Poppins', sans-serif"
  },
  guideSectionValue: {
    fontSize: '13px',
    color: '#4E5968',
    fontFamily: "'Poppins', sans-serif"
  },
  guideTable: {
    '& .MuiTableCell-root': {
      padding: '10px 12px',
      fontSize: '12px',
      fontFamily: "'Poppins', sans-serif",
      borderBottom: '1px solid #E5E8EB'
    }
  },
  guideTableHead: {
    backgroundColor: '#F7F8FA',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#191F28',
      fontSize: '11px'
    }
  },
  guideTableBody: {
    '& .MuiTableCell-body': {
      color: '#4E5968',
      fontSize: '11px'
    }
  },
  guideSpecName: {
    fontWeight: 600,
    color: '#191F28',
    backgroundColor: '#F7F8FA'
  },
  noImageGuide: {
    padding: '40px 20px',
    textAlign: 'center' as const,
    color: '#8B95A1',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif"
  }
}));

const NavRight: FC = () => {
  const classes = useStyles();
  const [currentTab, setCurrentTab] = useState<string>('contents');
  const currentTemplate = useSelector(
    (state: RootState) => state.template.templateTitle
  );

  const tabs = [
    { value: 'contents', label: 'CONTENTS' },
    { value: 'button', label: 'BUTTON' },
    { value: 'guide', label: 'GUIDE' }
  ];

  const handleTabsChange = (value: string): void => {
    setCurrentTab(value);
  };

  const guide = TEMPLATES_EDITOR_GUIDE?.[currentTemplate]?.guide;
  const specs = guide?.specs || [];
  const hasMultipleSpecs = specs.length > 1 && specs.some((s: GuideSpec) => s.name);

  const renderGuideTable = () => {
    if (!guide || specs.length === 0) {
      return (
        <div className={classes.noImageGuide}>
          This template does not require image upload.
        </div>
      );
    }

    if (hasMultipleSpecs) {
      // Multiple specs with names (like Carousel)
      return (
        <TableContainer>
          <Table className={classes.guideTable} size="small">
            <TableHead className={classes.guideTableHead}>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Display Size</TableCell>
                <TableCell>Recommended</TableCell>
                <TableCell>Crop</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.guideTableBody}>
              {specs.map((spec: GuideSpec, idx: number) => (
                <TableRow key={idx}>
                  <TableCell className={classes.guideSpecName}>
                    {spec.name}
                  </TableCell>
                  <TableCell>{spec.displaySize}</TableCell>
                  <TableCell>{spec.recommendedSize}</TableCell>
                  <TableCell>{spec.cropPosition || 'Center'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Single spec
    const spec = specs[0];
    return (
      <TableContainer>
        <Table className={classes.guideTable} size="small">
          <TableHead className={classes.guideTableHead}>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.guideTableBody}>
            <TableRow>
              <TableCell className={classes.guideSpecName}>
                Display Size
              </TableCell>
              <TableCell>{spec.displaySize}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.guideSpecName}>
                Recommended Upload
              </TableCell>
              <TableCell>{spec.recommendedSize}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.guideSpecName}>Ratio</TableCell>
              <TableCell>{spec.ratio}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.guideSpecName}>
                Crop Position
              </TableCell>
              <TableCell>{spec.cropPosition || 'Center cropped'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.tabsWrapper}>
        <div className={classes.tabs}>
          {tabs.map(tab => (
            <div
              key={tab.value}
              className={
                currentTab === tab.value ? classes.actTab : classes.tab
              }
              onClick={() => handleTabsChange(tab.value)}
            >
              {tab.value.toUpperCase()}
            </div>
          ))}
        </div>
        <Divider />
      </div>
      <Grid className={classes.tabScroll}>
        <Box>
          {currentTab !== 'guide' && (
            <>
              {TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.multi && (
                <>
                  <EditMulti />
                  <SwitchAutoRolling />
                </>
              )}
              {TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.dimmed && (
                <EditBgDimmed
                  menuTitle="BACKGROUND OPACITY"
                  type="bgDimmed"
                  max={100}
                />
              )}
              {TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.text?.convert && (
                <EditButtonType />
              )}
              {TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.darkMode && (
                <>
                  <SwitchDarkMode />
                  <SwitchSlidePosition />
                </>
              )}
              {TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.text
                ?.switchImageType && <SwitchImageType />}
              {TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.text
                ?.switchMediaType && <SwitchMediaType />}
            </>
          )}
          {currentTab === 'contents' &&
            TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.text && (
              <Text datas={TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.text} />
            )}
          {currentTab === 'button' &&
            TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.button && (
              <Button
                datas={TEMPLATES_EDITOR_REGARCY?.[currentTemplate]?.button}
              />
            )}
          {currentTab === 'guide' && (
              <div className={classes.guideContainer}>
                <div className={classes.guideSection}>
                  <div className={classes.guideSectionTitle}>Image Type</div>
                  <div className={classes.guideSectionValue}>
                    {guide?.imageType || 'None'}
                  </div>
                </div>
                {renderGuideTable()}
              </div>
            )}
        </Box>
      </Grid>
    </div>
  );
};

export default NavRight;
